import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Link } from "react-router-dom";
import useSocket from "src/_common/hooks/useSocket";
import {
  useAppEvenListSelector,
} from "src/_common/hooks/selectors/eventSelector";
import {
  CRYPTO_SECRET_KEY,
  INSIDERODEO_EVENT_STORAGE,
  paginate,
  PER_PAGE,
  SELECTED_EVENT_STORAGE,
  SOCKET_CHANNEL,
  SOCKET_URL,
  STORAGE,
  URLS,
  SOCKET_TRANSPORTS
} from "src/_config";
import { useEventApi } from "src/_common/hooks/actions/event/appEventApiHook";
import { toast } from "react-toastify";
import clsx from "clsx";
import { useAppEventAction } from "src/_common/hooks/actions/event/appEventActionHook";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useAppUserAction } from "src/_common/hooks/actions/user/appUserActionHook";
import Sponsor from "src/components/common/Sponsor";
import VideoModal from "../modal/videoModal";
import Cryptr from "cryptr";
import queryString from "query-string";
const cryptr = new Cryptr(CRYPTO_SECRET_KEY);

type Performance = {
  id?: number;
  sponsor?: { file_mime?: string; original?: string };
};
type EventListItem = {
  id: number;
  title?: string;
  location?: string;
  avatar?: { thumb?: string };
  rodeo_event_game_info?: { enable_header?: number | boolean; is_event_glow?: number };
  status_msg?: string;
  status?: string;
  performances?: Performance[];
  settings?: { is_event_prize?: boolean; is_game_prize?: boolean };
};

export default function RodeoEventPage() {
  const eventApi = useEventApi();
  const history = useHistory();
  const location = useLocation();
  const eventAction = useAppEventAction();
  const eventListSelector = useAppEvenListSelector() as { list?: EventListItem[] } | null;
  const [eventListData, setEventListData] = useState<EventListItem[]>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [isFetching, setIsFetching] = useState(false);
  const userApi = useUserApi();
  const userAction = useAppUserAction();
  const [gameId, setGameId] = useState<string | null>(null);
  const [selectedPerformance, setSelectedPerformance] = useState<Performance | null>(null);
  const [selectedRodeoEvent, setSelectedRodeoEvent] = useState<EventListItem | null>(null);
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);
  const [url, setUrl] = useState<string>("");
  const [promoType, setPromoType] = useState<string>("");

  const gotoPerformancesPage = (e: React.MouseEvent<HTMLAnchorElement>, re: EventListItem) => {
    e.preventDefault();
    setSelectedRodeoEvent(re);
    const eventId = cryptr.encrypt(String(re.id));
    localStorage.setItem(SELECTED_EVENT_STORAGE, eventId);
    if (re.rodeo_event_game_info?.enable_header !== undefined) {
      eventAction.setSiteHeader(Boolean(re.rodeo_event_game_info.enable_header));
    }
    if (re.status_msg) {
      eventAction.setStatusMsg(String(re.status_msg));
    }
    if (re?.performances?.length != 1) {
      history.push(`/performances/${eventId}`);
    } else {
      goToGameListingPage(e, re?.performances[0], re);
    }
  };

  const goToGameListingPage = (e: React.MouseEvent<HTMLAnchorElement> | null, performance: Performance | undefined, re: EventListItem) => {
    e?.preventDefault();
    if (re.rodeo_event_game_info?.enable_header !== undefined) {
      eventAction.setSiteHeader(Boolean(re.rodeo_event_game_info.enable_header));
    }
    if (re.status) {
      eventAction.setStatusMsg(String(re.status));
    }
    if (!performance) {
      return;
    }
    setSelectedPerformance(performance);
    const seen = checkPromoVidSeen(performance);
    // console.log({seen})
    if (!seen) {
      const vid = performance?.sponsor;
      const type = vid?.file_mime?.substring(0, vid?.file_mime?.indexOf("/"));
      if (type) {
        setPromoType(type);
      }
      if (vid?.original) {
        setUrl(vid.original);
      }
      if (type == "video" || type == "image") {
        setShowVideoModal(true);
      } else {
        changePage(performance, re);
      }
    } else {
      changePage(performance, re);
    }
  };

  const changePage = (performance: Performance, re: EventListItem) => {
    if (!performance.id) {
      return;
    }
    const eventId = cryptr.encrypt(re.id);
    const performanceId = cryptr.encrypt(performance.id);
    if (re?.settings?.is_event_prize || re?.settings?.is_game_prize) {
      eventAction.rodeoEventSettings(re?.settings);
      if (!re?.settings?.is_event_prize && re?.settings?.is_game_prize) {
        history.push(`/prize/${eventId}/${performanceId}/${cryptr.encrypt("Game")}`);
      } else {
        history.push(`/prizes/${eventId}/${performanceId}`);
      }
    } else {
      history.push(`/event/${eventId}/${performanceId}`);
    }
  };

  const checkPromoVidSeen = (performance: Performance) => {
    const x = localStorage.getItem("performanceVidSeen_" + String(performance?.id || ''));
    // console.log({x})
    if (x && x == "1") {
      return true;
    } else {
      return false;
    }
  };

  const closeVideoModal = () => {
    setTimeout(() => {
      setShowVideoModal(false);
      if (selectedPerformance?.id && selectedRodeoEvent) {
        localStorage.setItem("performanceVidSeen_" + selectedPerformance.id, "1");
        changePage(selectedPerformance, selectedRodeoEvent);
      }
    }, 1000);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isFetching) return;
    fetchMoreListItems();
  }, [isFetching]);

  // Below function excute the scroll operation text show or hide
  function handleScroll() {
    if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight || isFetching)
      return;
    if (eventListData.length === total) return; //Disable scrolling state if total event data is equal to total data
    setIsFetching(true);
  }

  //Get event List
  const getEventList = () => {
    const params = {
      page: page,
      perPage: PER_PAGE,
    };

    eventApi.callGetEventList(
      params,
      (_message: string, resp: unknown) => {
        if (resp && typeof resp === "object" && "list" in resp) {
          const respData = resp as { list?: Array<EventListItem> & { total?: number }; game_details?: { id?: string } };
          if (respData.list && respData.list.length) {
            const nextItems = paginate(respData.list as EventListItem[], PER_PAGE, page) as EventListItem[];
            setEventListData((prevState) => [...prevState, ...nextItems]);
            setTotal(respData.list.total ? parseInt(String(respData.list.total)) : 0);
            setPage(page + 1);
            setIsFetching(false);
            if (respData.game_details?.id) {
              setGameId(String(respData.game_details.id));
            }
          } else {
            setEventListData((prevState) => [...prevState]);
            setIsFetching(false);
          }
        }
      },
      (message: string) => {
        toast.error(message);
      }
    );
  };

  //On page scroll load more data
  // Event api call once again
  const fetchMoreListItems = () => {
    setTimeout(() => {
      getEventList();
    }, 2000);
  };

  const getStatusMessage = () => {
    const params = {
      game_id: gameId,
    };
    eventApi.callGetGameStatusText(
      params,
      (_message: string) => {},
      (_message: string) => {}
    );
  };

  useEffect(() => {
    if (gameId) {
      getStatusMessage();
    }
  }, [gameId]);

  //First Time call When component is load
  useEffect(() => {
    getEventList();
  }, []);

  useEffect(() => {
    localStorage.removeItem(INSIDERODEO_EVENT_STORAGE);

    const parsedHash = queryString.parse(location.hash);
    const userToken =
      typeof parsedHash.userToken === "string"
        ? parsedHash.userToken
        : Array.isArray(parsedHash.userToken)
        ? parsedHash.userToken[0]
        : undefined;
    const eventToken =
      typeof parsedHash.token === "string" ? parsedHash.token : Array.isArray(parsedHash.token) ? parsedHash.token[0] : undefined;
    // console.log(parsedHash)
    if (userToken && userToken != "null") {
      localStorage.setItem("parshedHash", "1");
      userAction.resetGuestUserId();
      localStorage.removeItem("guestUserId");
      localStorage.setItem(STORAGE, userToken);
      userApi.callGetMe(
        (_message: string) => {},
        (_message: string) => {
          history.push(URLS.HOME);
          userAction.logout();
        }
      );
    }
    if (eventToken) {
      localStorage.setItem(INSIDERODEO_EVENT_STORAGE, eventToken);
      history.push(`/performances/${localStorage.getItem(INSIDERODEO_EVENT_STORAGE)}`);
    } else if (localStorage.getItem(INSIDERODEO_EVENT_STORAGE)) {
      history.push(`/performances/${localStorage.getItem(INSIDERODEO_EVENT_STORAGE)}`);
    } else {
      history.push(URLS.HOME);
    }
  }, []);

  const [socket] = useSocket(SOCKET_URL, {
    autoConnect: false,
    withCredentials: true,
    transports: SOCKET_TRANSPORTS,
    upgrade: true,        // Enable transport upgrades (polling -> websocket)
    rememberUpgrade: true, // Remember successful upgrades for faster reconnection
    
  });

  useEffect(() => {
    socket.connect();

    //Status Change Socket
    socket.on(SOCKET_CHANNEL.START_EVENT, (_changeEventStatus: unknown) => {});

    //Performance List Update
    socket.on(SOCKET_CHANNEL.SCAVENGER_PERFORMANCE, (_data: unknown) => {
      getEventList();
    });

    //Status Change Socket
    socket.on(SOCKET_CHANNEL.EVENT_GLOW, (changeEventStatus: Record<string, unknown>) => {
      console.log({ changeEventStatus });
      eventAction.updateEventGlowingStatus(changeEventStatus);
    });

    return () => {
      socket.disconnect();
    };
  }, [socket]);

  return (
    <React.Fragment>
      {url && showVideoModal && promoType && (
        <VideoModal onClose={closeVideoModal} url={url} shouldShow={showVideoModal} type={promoType} />
      )}
      {
        <div className="logo-wrap">
          <img
            src="/images/logo.png"
            alt="Logo"
            className="medium-logo"
            style={{ marginTop: "10px", marginBottom: "10px", width: "200px" }}
          />
        </div>
      }
      <section className="main-container">
        <h2 className="page-title text-left">
          <span>Rodeo </span>Events
        </h2>
        <hr className="color-white" />
        <ul className="list-style homepage-event-list">
          {eventListSelector?.list?.length ? (
            eventListSelector.list.map((event) => (
              <li
                key={event.id}
                className={clsx({
                  glow: event?.rodeo_event_game_info?.is_event_glow == 1,
                })}
              >
                <Link
                  to=""
                  onClick={(e) => gotoPerformancesPage(e, event)}
                  title={event.title}
                  className="ripple-effect"
                >
                  <span className="r-event-img">
                    <img
                      src={event.avatar?.thumb ? event.avatar.thumb : "images/no-image.png"}
                      alt={event.title || "Rodeo Event"}
                    />
                  </span>
                  <span className="r-event-info">
                    <span className="d-inline-block r-event-title">{event.title || "Rodeo Event"}</span>
                    <span className="d-block r-location">{event.location ? event.location : "..."}</span>
                  </span>
                </Link>
              </li>
            ))
          ) : (
            <div className="no-text text-white text-center">No events available</div>
          )}
        </ul>
        {isFetching && (
          <div style={{ color: "#fce006" }}>
            <p>Loading more events. Please wait... </p>
          </div>
        )}
        <Sponsor />
      </section>
    </React.Fragment>
  );
}
